import {
  alertIcon,
  checkCircleIcon,
  clockIcon,
  dashIcon,
  issueOpenedIcon,
  noEntryIcon,
  rewindFillIcon,
  sendCheckIcon,
  xCircleIcon
} from '../icons';

export type BillingClaimRemittanceOutcome =
  | 'unremitted'
  | 'paid'
  | 'partially_paid'
  | 'denied'
  | 'zero_paid'
  | 'reversal';

export type BillingClaimPrimaryStatusSource = '837' | '999' | '277' | '835';

export type BillingClaimPrimaryStatus =
  | 'unsubmitted'
  | '837_pending'
  | '837_accepted'
  | '837_accepted_with_errors'
  | '837_partially_accepted'
  | '837_rejected'
  | 'received'
  | 'accepted_for_adjudication'
  | 'forwarded'
  | 'rejected'
  | 'acknowledged'
  | BillingClaimRemittanceOutcome;

const billingClaimPrimaryStatusIcons: Record<
  BillingClaimPrimaryStatus,
  string
> = {
  unsubmitted: dashIcon,
  '837_pending': clockIcon,
  '837_accepted': checkCircleIcon,
  '837_accepted_with_errors': alertIcon,
  '837_partially_accepted': alertIcon,
  '837_rejected': xCircleIcon,
  received: issueOpenedIcon,
  accepted_for_adjudication: checkCircleIcon,
  forwarded: sendCheckIcon,
  rejected: xCircleIcon,
  acknowledged: checkCircleIcon,
  unremitted: clockIcon,
  paid: checkCircleIcon,
  partially_paid: alertIcon,
  denied: noEntryIcon,
  zero_paid: xCircleIcon,
  reversal: rewindFillIcon
};

export function claimPrimaryStatusIcon(
  status: BillingClaimPrimaryStatus
): string {
  return billingClaimPrimaryStatusIcons[status];
}

export const billingClaimPrimaryStatusIconClasses: Record<
  BillingClaimPrimaryStatus,
  string
> = {
  unsubmitted: 'color-fg-muted',
  '837_pending': 'color-fg-muted',
  '837_accepted': 'color-fg-success',
  '837_accepted_with_errors': 'color-fg-attention',
  '837_partially_accepted': 'color-fg-attention',
  '837_rejected': 'color-fg-danger',
  received: 'color-fg-success',
  accepted_for_adjudication: 'color-fg-success',
  forwarded: 'color-fg-success',
  rejected: 'color-fg-danger',
  acknowledged: 'color-fg-success',
  unremitted: 'color-fg-muted',
  paid: 'color-fg-success',
  partially_paid: 'color-fg-attention',
  denied: 'color-fg-danger',
  zero_paid: 'color-fg-danger',
  reversal: 'color-fg-attention'
};

export interface ClaimBatchClaimListItem {
  claim_id: string;
  claim_batch_id: string;
  submitter_claim_control_number: string;
  person_id: string;
  person_full_name: string;
  authorization_number: string | null;
  original_payer_claim_control_number: string | null;
  facility_type_code: string;
  claim_frequency_type_code: string;
  statement_period_start: string;
  statement_period_end: string;
  subscriber_id: string;
  subscriber_first_name: string | null;
  subscriber_middle_name: string | null;
  subscriber_last_name: string;
  service_line_count: number;
  total_charge_amount: number;
  activity_count: number;
  remittance_claim_count: number;
  paid_amount: number;
  patient_responsibility_amount: number;
  adjustment_amount: number;
  latest_remittance_claim_status_code: string | null;
  latest_remittance_claim_status_description: string | null;
  latest_payment_date: string | null;
  latest_adjudication_date: string | null;
  latest_payer_claim_control_number: string | null;
  latest_claim_acknowledgment_category_code: string | null;
  latest_claim_acknowledgment_category_description: string | null;
  latest_claim_acknowledgment_status_code: string | null;
  latest_claim_acknowledgment_status_description: string | null;
  latest_claim_acknowledgment_effective_date: string | null;
  latest_claim_acknowledgment_payer_claim_control_number: string | null;
  latest_claim_acknowledgment_payer_file_id: string | null;
  latest_claim_acknowledgment_filename: string | null;
  primary_adjustment_code: string | null;
  primary_adjustment_description: string | null;
  primary_remark_code: string | null;
  primary_remark_description: string | null;
  remittance_authorization_reference: string | null;
  remittance_outcome: BillingClaimRemittanceOutcome;
  primary_status_source: BillingClaimPrimaryStatusSource | null;
  primary_status: BillingClaimPrimaryStatus;
  primary_status_code: string | null;
  primary_status_description: string | null;
  primary_status_date: string | null;
  primary_status_payer_file_id: string | null;
  primary_status_filename: string | null;
  remittance_issue_count: number;
}

export interface ClaimBatchClaimGridRow extends ClaimBatchClaimListItem {
  claim_href: string;
  primary_status_label: string;
}

export interface ClaimAcknowledgmentEntryListItem {
  claim_acknowledgment_entry_id: string;
  payer_file_id: string;
  attachment_id: string;
  attachment_filename: string;
  claim_submission_id: string | null;
  claim_id: string | null;
  claim_batch_id: string | null;
  submitter_claim_control_number: string | null;
  clearinghouse_trace_number: string | null;
  payer_claim_control_number: string | null;
  patient_last_name: string | null;
  patient_first_name: string | null;
  payer_name: string | null;
  category_code: string;
  category_description: string | null;
  status_code: string | null;
  status_description: string | null;
  status_message: string | null;
  effective_date: string | null;
  total_charge: number | null;
  create_datetime: string;
}

export interface PayerFileClaimAcknowledgmentGridRow extends ClaimAcknowledgmentEntryListItem {
  claim_href: string | null;
}

export interface PayerFileRemittanceClaimListItem {
  remittance_claim_id: string;
  claim_id: string | null;
  claim_batch_id: string | null;
  submitter_claim_control_number: string;
  person_full_name: string | null;
  claim_configuration_title: string | null;
  project_login: string | null;
  billing_period_start: string | null;
  billing_period_end: string | null;
  claim_status_code: string;
  claim_status_description: string | null;
  submitted_charge_amount: number | null;
  paid_amount: number | null;
  patient_responsibility_amount: number | null;
  payer_claim_control_number: string | null;
  adjudication_date: string | null;
}

export interface PayerFileRemittanceClaimGridRow extends PayerFileRemittanceClaimListItem {
  claim_href: string | null;
  claim_batch_href: string | null;
}

export type ClaimBatchPreviewBillingEffect =
  | 'none'
  | 'affects_amount'
  | 'exclude_source'
  | 'exclude_claim'
  | 'block_batch';

export type ClaimBatchPreviewReviewLevel = 'info' | 'attention' | 'action';

export interface ClaimBatchPreviewIssue {
  billing_effect: ClaimBatchPreviewBillingEffect;
  review_level: ClaimBatchPreviewReviewLevel;
  code: string;
  message: string;
  activity_id: string | null;
  person_full_name: string | null;
  activity_definition_title: string | null;
  activity_start_date: string | null;
  activity_end_date: string | null;
  claim_id: string | null;
  claim_service_line_id: string | null;
}

export interface ClaimBatchPreviewIssueGridRow extends ClaimBatchPreviewIssue {
  row_id: string;
  activity_href: string | null;
}
